// Partner Rate Campaigns — compose country-segmented outbound rate offers
// using live CMA CGM SpotOn rates (Koper → Asia). Recipients are pulled from
// the Partner Directory and segmented by country.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import OffersModuleLayout from "./OffersModuleLayout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  RefreshCw, Send, Eye, Megaphone, Ship, Users, Sparkles, Loader2, AlertTriangle, Clock
} from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerCampaignsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rates, setRates] = useState(null);
  const [recipients, setRecipients] = useState({});
  const [selected, setSelected] = useState([]);
  const [useAi, setUseAi] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentHistory, setSentHistory] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ratesRes, recRes, sendsRes] = await Promise.all([
        axios.get(`${API_URL}/partner-campaigns/rates`, authHeader()),
        axios.get(`${API_URL}/partner-campaigns/recipients`, authHeader()),
        axios.get(`${API_URL}/partner-campaigns/sends?limit=200`, authHeader()),
      ]);
      setRates(ratesRes.data);
      const map = {};
      (recRes.data.countries || []).forEach((c) => { map[c.country_code] = c; });
      setRecipients(map);
      const hist = {};
      (sendsRes.data.sends || []).forEach((s) => {
        if (s.success && !s.test && !hist[s.country_code]) hist[s.country_code] = s;
      });
      setSentHistory(hist);
    } catch (e) {
      toast.error("Failed to load campaign data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const refreshRates = async () => {
    setRefreshing(true);
    toast.info("Fetching live CMA rates in the background… the table updates as rates arrive.");
    try {
      await axios.post(`${API_URL}/partner-campaigns/rates/refresh`, {}, authHeader());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start rate refresh");
      setRefreshing(false);
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const s = await axios.get(`${API_URL}/partner-campaigns/rates/refresh-status`, authHeader());
        const m = s.data || {};
        await fetchAll();
        if (m.running && tries < 90) {
          setTimeout(poll, 4000);
        } else {
          setRefreshing(false);
          if (m.aborted === "edge_rate_limit") {
            toast.warning("CMA edge rate-limit hit — partial rates cached. Retry in a few minutes.");
          } else if (m.running) {
            toast.info("Refresh still running — reload later to see all rates.");
          } else {
            toast.success(`Rates updated: ${m.fetched ?? 0} priced, ${m.failed ?? 0} unavailable.`);
          }
        }
      } catch (e) {
        if (tries < 90) {
          setTimeout(poll, 4000);
        } else {
          setRefreshing(false);
          toast.error("Lost track of refresh status — reload to see the latest rates.");
        }
      }
    };
    setTimeout(poll, 4000);
  };

  const toggleCountry = (cc) => {
    setSelected((prev) => prev.includes(cc) ? prev.filter((x) => x !== cc) : [...prev, cc]);
  };

  const doPreview = async (cc) => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await axios.post(`${API_URL}/partner-campaigns/preview`,
        { country_code: cc, use_ai_intro: useAi }, authHeader());
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    if (!selected.length) return toast.error("Select at least one country");
    const myEmail = user?.email || "rok.kobal@europacific.com";
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/partner-campaigns/send`,
        { country_codes: selected, use_ai_intro: useAi, test_recipient: myEmail },
        authHeader());
      toast.success(`Test sent: ${res.data.sent} ok, ${res.data.failed} failed (to ${myEmail}).`, { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed", { duration: 7000 });
    } finally {
      setSending(false);
    }
  };

  const sendLive = () => {
    if (!selected.length) return toast.error("Select at least one country");
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      await axios.post(`${API_URL}/partner-campaigns/send`,
        { country_codes: selected, use_ai_intro: useAi }, authHeader());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start campaign send", { duration: 7000 });
      setSending(false);
      return;
    }
    toast.info("Sending campaign in the background… you'll see a summary when it finishes.", { duration: 5000 });
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const s = await axios.get(`${API_URL}/partner-campaigns/send-status`, authHeader());
        const m = s.data || {};
        if (m.running && tries < 150) {
          setTimeout(poll, 3000);
        } else {
          setSending(false);
          const results = m.results || [];
          const errs = results.filter((r) => !r.success);
          if (m.sent) toast.success(`Campaign sent to ${m.sent} countr${m.sent > 1 ? "ies" : "y"} (${results.filter((r) => r.success).reduce((a, r) => a + (r.bcc_count || 0), 0)} agents).`, { duration: 7000 });
          if (errs.length) toast.warning(`${errs.length} failed: ${errs.map((e) => `${e.country_code} (${e.error})`).join(", ")}`, { duration: 10000 });
          fetchAll();
        }
      } catch (e) {
        if (tries < 150) { setTimeout(poll, 3000); }
        else { setSending(false); toast.error("Lost track of send status — check the Last-sent badges."); }
      }
    };
    setTimeout(poll, 3000);
  };

  const countries = rates?.countries || [];
  const hasAnyRates = countries.some((c) => c.pods.some((p) => Object.values(p.rates).some((r) => r.ok && r.sell != null)));

  return (
    <OffersModuleLayout>
      <div className="max-w-[1800px] mx-auto px-6 py-8" data-testid="partner-campaigns-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="h-6 w-6 text-primary" /> Partner Rate Campaigns
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Live CMA CGM SpotOn rates · Koper → 16 Asian ports · FAK · +{rates?.markup_usd_per_teu ?? 50} USD/TEU markup ·
              valid {rates?.validity_days ?? 7} days. Recipients segmented by country.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm mr-2 cursor-pointer" data-testid="campaign-ai-toggle">
              <Checkbox checked={useAi} onCheckedChange={(v) => setUseAi(!!v)} />
              <Sparkles className="h-4 w-4 text-amber-500" /> AI intro line
            </label>
            <Button variant="outline" onClick={refreshRates} disabled={refreshing}
              data-testid="campaign-refresh-rates-btn">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh CMA Rates
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {!hasAnyRates && (
              <Card className="p-4 mb-6 bg-amber-50 border-amber-200 flex items-start gap-3" data-testid="campaign-no-rates-notice">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  No cached rates yet. Click <strong>Refresh CMA Rates</strong> to fetch live SpotOn pricing.
                  This runs automatically every night in production.
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country matrix */}
              <div className="space-y-4">
                {countries.map((c) => {
                  const rec = recipients[c.country_code] || {};
                  const isSel = selected.includes(c.country_code);
                  const priced = c.pods.filter((p) => Object.values(p.rates).some((r) => r.ok && r.sell != null));
                  return (
                    <Card key={c.country_code} className={`p-4 transition-all ${isSel ? "ring-2 ring-primary" : ""}`}
                      data-testid={`campaign-country-${c.country_code}`}>
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <Checkbox checked={isSel} onCheckedChange={() => toggleCountry(c.country_code)}
                            data-testid={`campaign-select-${c.country_code}`} />
                          <div>
                            <div className="font-semibold text-slate-900 flex items-center gap-2">
                              <Ship className="h-4 w-4 text-primary" /> {c.country}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Users className="h-3 w-3" /> {rec.email_count || 0} agents · {rec.partner_count || 0} companies
                            </div>
                            {sentHistory[c.country_code] && (
                              <div className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5"
                                data-testid={`campaign-lastsent-${c.country_code}`}>
                                <Clock className="h-3 w-3" /> Last sent {fmtDate(sentHistory[c.country_code].sent_at)} · {sentHistory[c.country_code].bcc_count} agents
                              </div>
                            )}
                          </div>
                        </label>
                        <Button size="sm" variant="ghost" onClick={() => doPreview(c.country_code)}
                          disabled={!priced.length} data-testid={`campaign-preview-${c.country_code}`}>
                          <Eye className="h-4 w-4 mr-1" /> Preview
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-muted-foreground border-b">
                              <th className="text-left py-1 font-medium">Destination</th>
                              {(rates.equipment || []).map((e) => (
                                <th key={e.code} className="text-right py-1 font-medium">{e.code}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {c.pods.map((p) => (
                              <tr key={p.pod} className="border-b border-slate-100">
                                <td className="py-1.5 font-medium text-slate-700">{p.pod_name}</td>
                                {(rates.equipment || []).map((e) => {
                                  const r = p.rates[e.code];
                                  return (
                                    <td key={e.code} className="text-right py-1.5">
                                      {r && r.ok && r.sell != null
                                        ? <span className="font-semibold text-slate-900">${r.sell.toLocaleString()}</span>
                                        : <span className="text-slate-400">—</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Preview + send panel */}
              <div className="space-y-4">
                <Card className="p-4 sticky top-32" data-testid="campaign-send-panel">
                  <h3 className="font-semibold text-slate-900 mb-2">Campaign Composer</h3>
                  <div className="text-sm text-muted-foreground space-y-1 mb-4">
                    <div>From: <strong>{rates?.sender || "—"}</strong></div>
                    <div>To: {rates?.to_email || "—"} · CC: {rates?.cc_email || "—"}</div>
                    <div>BCC: selected country agents</div>
                    <div className="pt-1">Selected: <strong>{selected.length}</strong> countries ·{" "}
                      <strong>{selected.reduce((s, cc) => s + (recipients[cc]?.email_count || 0), 0)}</strong> agents</div>
                  </div>
                  <div className="flex gap-2 mb-4">
                    <Button variant="outline" onClick={sendTest} disabled={sending || !selected.length}
                      className="flex-1" data-testid="campaign-send-test-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test (to me)
                    </Button>
                    <Button onClick={sendLive} disabled={sending || !selected.length}
                      className="flex-1 bg-green-600 hover:bg-green-700" data-testid="campaign-send-live-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Campaign
                    </Button>
                  </div>

                  {previewing && (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…
                    </div>
                  )}
                  {preview && !previewing && (
                    <div data-testid="campaign-preview">
                      <div className="text-xs text-muted-foreground mb-1">Subject</div>
                      <div className="text-sm font-medium mb-2">{preview.subject}</div>
                      <div className="text-xs text-muted-foreground mb-2">
                        BCC {preview.bcc_count} agents in {preview.country}
                      </div>
                      <div className="border rounded-lg overflow-hidden bg-white" style={{ maxHeight: 520, overflowY: "auto" }}>
                        <div dangerouslySetInnerHTML={{ __html: preview.html }} />
                      </div>
                    </div>
                  )}
                  {!preview && !previewing && (
                    <div className="text-sm text-muted-foreground text-center py-8">
                      Click <strong>Preview</strong> on a country to see the email.
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="campaign-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Send live campaign?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  This emails {selected.length} countr{selected.length > 1 ? "ies" : "y"} —{" "}
                  <strong>{selected.reduce((s, cc) => s + (recipients[cc]?.email_count || 0), 0)} partner agents</strong> (BCC).
                  <br />From {rates?.sender} · To {rates?.to_email} · CC {rates?.cc_email}.
                  <br />Countries: {selected.join(", ") || "—"}.
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="campaign-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSend} className="bg-green-600 hover:bg-green-700"
                data-testid="campaign-confirm-send">
                Send to {selected.reduce((s, cc) => s + (recipients[cc]?.email_count || 0), 0)} agents
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </OffersModuleLayout>
  );
}
